import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/SearchableSelect";
import PhoneInput from "@/components/PhoneInput";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export const YH_CATS = ["Otomobil", "SUV / Pickup", "Hafif ticari", "Kamyon", "Tır / Çekici", "Otobüs / Minibüs", "İş makinesi", "Traktör", "Forklift", "Diğer"];
const CONDS = ["Çalışıyor", "Çalışmıyor", "Hasarlı"];
export const YH_MODEL_LABEL = { model_1: "Model 1 · Yol Hattı adına", model_2: "Model 2 · Müşteri adına", model_3: "Model 3 · Doğrudan müşteri iletişimi" };
const MODEL_HINT = {
  model_1: "Yol Hattı'nın kendi adına açtığı ilan.",
  model_2: "Müşteri adına açılır; müşteri bilgileri sağlayıcılara gösterilmez, iletişim Yol Hattı üzerinden yürür.",
  model_3: "Taslak olarak kaydedilir. Müşteri onay bağlantısından KVKK onayı verince havuzda yayınlanır; onaylı sağlayıcılar müşteriyi doğrudan arar.",
};
const EMPTY = {
  yh_model: "model_1", customer_name: "", customer_phone: "", yh_price_tl: "", yh_pricing_method: "fixed",
  pickup_province: "", pickup_district: "", pickup_address: "",
  delivery_province: "", delivery_district: "", delivery_address: "",
  deadline_at: "", transport_category: "Otomobil", quantity: 1, running_condition: "Çalışıyor",
  make_model: "", description: "",
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const useDistricts = (province) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!province) { setList([]); return; }
    api.get("/locations/districts", { params: { province } }).then(({ data }) => setList(data));
  }, [province]);
  return list;
};

const RouteFields = ({ side, f, upd, provinces, disabled }) => {
  const districts = useDistricts(f[`${side}_province`]);
  const label = side === "pickup" ? "Alım" : "Teslim";
  return (
    <div className="space-y-2">
      <Label className="font-semibold">{label} noktası</Label>
      <div className="grid grid-cols-2 gap-2">
        <SearchableSelect testid={`yh-${side}-prov`} value={f[`${side}_province`]} disabled={disabled}
          onChange={(v) => { upd(`${side}_province`)(v); upd(`${side}_district`)(""); }} options={provinces} placeholder="İl" />
        <SearchableSelect testid={`yh-${side}-dist`} value={f[`${side}_district`]} onChange={upd(`${side}_district`)}
          options={districts} placeholder="İlçe" disabled={disabled || !f[`${side}_province`]} />
      </div>
      <Input placeholder={`${label} açık adresi`} value={f[`${side}_address`]}
        onChange={upd(`${side}_address`)} data-testid={`yh-${side}-address`} />
    </div>
  );
};

const ModelAndCustomer = ({ f, upd, editing }) => (
  <div className="space-y-3">
    <div>
      <Label className="font-semibold">İş modeli</Label>
      <Select value={f.yh_model} onValueChange={upd("yh_model")} disabled={editing}>
        <SelectTrigger className="mt-1" data-testid="yh-model"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="model_1" data-testid="yh-model-1">{YH_MODEL_LABEL.model_1}</SelectItem>
          <SelectItem value="model_2" data-testid="yh-model-2">{YH_MODEL_LABEL.model_2}</SelectItem>
          <SelectItem value="model_3" data-testid="yh-model-3">{YH_MODEL_LABEL.model_3}</SelectItem>
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground mt-1" data-testid="yh-model-hint">{MODEL_HINT[f.yh_model]}</p>
    </div>
    {f.yh_model !== "model_1" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-dashed p-3" data-testid="yh-customer-fields">
        <div><Label>Müşteri adı soyadı</Label><Input value={f.customer_name} onChange={upd("customer_name")} className="mt-1" data-testid="yh-customer-name" /></div>
        <div><Label>Müşteri cep telefonu</Label><div className="mt-1"><PhoneInput value={f.customer_phone} onChange={upd("customer_phone")} testid="yh-customer-phone" /></div></div>
        <p className="sm:col-span-2 text-xs text-muted-foreground">{f.yh_model === "model_3" ? "Müşteri KVKK onayı verdikten sonra yalnız giriş yapmış, onaylı sağlayıcılara gösterilir." : "Bu bilgiler yalnız yöneticiler tarafından görülür."}</p>
      </div>
    )}
    {f.yh_model === "model_3" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="yh-price-fields">
        <div><Label>Fiyat tipi</Label>
          <Select value={f.yh_pricing_method} onValueChange={upd("yh_pricing_method")}>
            <SelectTrigger className="mt-1" data-testid="yh-pricing-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed" data-testid="yh-pricing-fixed">Sabit fiyat</SelectItem>
              <SelectItem value="negotiable" data-testid="yh-pricing-negotiable">Pazarlığa açık</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Taşıma fiyatı (TL)</Label><Input type="number" min={1} value={f.yh_price_tl} onChange={upd("yh_price_tl")} className="mt-1" data-testid="yh-price" /></div>
      </div>
    )}
  </div>
);

export const YhListingFormDialog = ({ open, onClose, listing, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [provinces, setProvinces] = useState([]);
  const [busy, setBusy] = useState(false);
  const editing = !!listing;
  const routeLocked = editing && !["OPEN", "DRAFT"].includes(listing.status);

  useEffect(() => { api.get("/locations/provinces").then(({ data }) => setProvinces(data)); }, []);
  useEffect(() => {
    if (!open) return;
    if (!listing) { setF(EMPTY); return; }
    const snap = listing.yh_customer_snapshot || {};
    setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, listing[k] ?? EMPTY[k]])),
      customer_name: snap.name || "", customer_phone: snap.phone || "", deadline_at: toLocalInput(listing.deadline_at) });
  }, [open, listing]);

  const upd = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.pickup_province || !f.pickup_district || !f.delivery_province || !f.delivery_district) return toast.error("Alım ve teslim il/ilçe zorunludur");
    if (!f.deadline_at) return toast.error("En geç alınma tarihi zorunludur");
    const isM2 = f.yh_model !== "model_1";
    const isM3 = f.yh_model === "model_3";
    if (isM2 && (f.customer_name.trim().length < 2 || !/^\+905\d{9}$/.test(f.customer_phone || ""))) return toast.error("Müşteri adı ve geçerli cep telefonu zorunludur");
    if (isM3 && (f.pickup_address.trim().length < 5 || f.delivery_address.trim().length < 5)) return toast.error("Model 3 için alım ve teslim açık adresleri zorunludur");
    if (isM3 && !(Number(f.yh_price_tl) > 0)) return toast.error("Model 3 için taşıma fiyatı zorunludur");
    const { customer_name, customer_phone, yh_model, yh_price_tl, yh_pricing_method, ...rest } = f;
    const snapshot = isM2 ? { yh_customer_snapshot: { name: customer_name.trim(), phone: customer_phone } } : {};
    const price = isM3 ? { yh_price_tl: Number(yh_price_tl), yh_pricing_method } : {};
    const payload = { ...rest, ...price, quantity: Number(f.quantity) || 1, deadline_at: new Date(f.deadline_at).toISOString() };
    setBusy(true);
    try {
      if (editing) {
        const body = routeLocked ? { pickup_address: f.pickup_address, delivery_address: f.delivery_address, description: f.description } : payload;
        await api.patch(`/admin/transport/listings/${listing.id}`, { ...body, ...(routeLocked && isM3 ? {} : snapshot) });
        toast.success(isM3 && listing.status === "OPEN" ? "İlan güncellendi (müşteri bilgisi değiştiyse yeni onay gerekir)" : "İlan güncellendi");
      } else {
        await api.post("/admin/transport/listings", { ...payload, ...snapshot, yh_model });
        toast.success(isM3 ? "Taslak kaydedildi. Detaydan müşteri onay bağlantısını oluşturun." : "İlan yayınlandı, taşıma havuzunda görünüyor");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="yh-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "İlanı Düzenle" : "Yeni Yol Hattı İlanı"}</DialogTitle></DialogHeader>
        {routeLocked && <div className="text-xs rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-800" data-testid="yh-form-locked-note">Teklif kabul edildiği için yalnız adres ve açıklama değiştirilebilir.</div>}
        <form onSubmit={submit} className="space-y-4">
          <ModelAndCustomer f={f} upd={upd} editing={editing} />
          <RouteFields side="pickup" f={f} upd={upd} provinces={provinces} disabled={routeLocked} />
          <RouteFields side="delivery" f={f} upd={upd} provinces={provinces} disabled={routeLocked} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div><Label>Kategori</Label>
              <Select value={f.transport_category} onValueChange={upd("transport_category")} disabled={routeLocked}>
                <SelectTrigger data-testid="yh-cat" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{YH_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Adet</Label><Input type="number" min={1} value={f.quantity} onChange={upd("quantity")} className="mt-1" disabled={routeLocked} data-testid="yh-qty" /></div>
            <div><Label>Durum</Label>
              <Select value={f.running_condition} onValueChange={upd("running_condition")} disabled={routeLocked}>
                <SelectTrigger className="mt-1" data-testid="yh-cond"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Label>Marka / Model (ops.)</Label><Input value={f.make_model} onChange={upd("make_model")} className="mt-1" disabled={routeLocked} data-testid="yh-make" /></div>
            <div><Label>En Geç Alınma Tarihi</Label><Input type="datetime-local" value={f.deadline_at} onChange={upd("deadline_at")} className="mt-1" disabled={routeLocked} data-testid="yh-deadline" /></div>
          </div>
          <div><Label>Açıklama (ops.)</Label><Textarea value={f.description} onChange={upd("description")} rows={2} data-testid="yh-desc" placeholder="Telefon/e-posta yazmayın" /></div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="yh-form-cancel">Vazgeç</Button>
            <Button type="submit" disabled={busy} data-testid="yh-form-submit" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{editing ? "Kaydet" : f.yh_model === "model_3" ? "Taslak Kaydet" : "İlanı Yayınla"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
